import { isTagNode } from '../../../typeUtils';
import { flatten, isEmpty } from 'lodash';

const splitIntoTwoArraysByPredicate = (node = {}, fn, extractSortOrder) => {
  if (Array.isArray(node)) {
    return node.reduce(
      (accm, val) => {
        if (fn(val)) {
          return [[...accm[0], val], accm[1]];
        }
        return [accm[0], [...accm[1], val]];
      },
      [[], []]
    );
  }

  return Object.entries(node).reduce(
    (accm, [key, val]) => {
      if (fn(val)) {
        return [
          [...accm[0], { key, sortOrder: extractSortOrder(val) }],
          accm[1],
        ];
      }
      return [accm[0], [...accm[1], key]];
    },
    [[], []]
  );
};

export const bySortOrder = (a, b) => a.sortOrder - b.sortOrder;

export const byMetaDataSortOrder = (a, b) =>
  a.metaDataSortOrder - b.metaDataSortOrder;

export const sortByAbc = (a, b) =>
  a.name < b.name ? -1 : a.name > b.name ? 1 : 0;

export const sortNodesBySortOrderFirst = (node) => {
  if (isEmpty(node)) {
    return [];
  }

  const [withSortOrder, withoutSortOrder] = splitIntoTwoArraysByPredicate(
    node,
    (obj) => obj.data && (obj.data.sortOrder || obj.data.sortOrder === 0),
    (obj) => obj.data.sortOrder
  );

  const keysSortedBySortOrder = withSortOrder
    .sort(bySortOrder)
    .map(({ key }) => key);

  const hasTags = node?.data?.tags;
  const isTag = isTagNode(node);

  const shouldSortByAlphabet = !(hasTags || isTag);

  const sortedByAlphabet = shouldSortByAlphabet
    ? withoutSortOrder.sort()
    : withoutSortOrder;

  return [...keysSortedBySortOrder, ...sortedByAlphabet];
};

export const sortMembersBySortOrderFirst = (node) => {
  const [withSortOrder, withoutSortOrder] = splitIntoTwoArraysByPredicate(
    node,
    (obj) => obj && (obj.sortOrder || obj.sortOrder === 0),
    (obj) => obj.sortOrder
  );
  withSortOrder.sort(bySortOrder);

  const rearrangedMembers = [...withSortOrder, ...withoutSortOrder];

  return rearrangedMembers;
};

export const sortRelatedGuidesBySortOrderFirst = (relatedGuides = []) => {
  const doesGuideHaveSortOrder = (guide) =>
    guide && typeof guide.sortOrder === 'number';

  relatedGuides.sort(byMetaDataSortOrder);

  const relatedGuidesChunks = [];
  let isCurrentChunkForGuidesWithMenuConfigSortOrder = doesGuideHaveSortOrder(
    relatedGuides[0]
  );
  let currentChunk = [];
  relatedGuides.forEach((guide, i) => {
    const isNewChunk =
      (doesGuideHaveSortOrder(guide) &&
        !isCurrentChunkForGuidesWithMenuConfigSortOrder) ||
      (!doesGuideHaveSortOrder(guide) &&
        isCurrentChunkForGuidesWithMenuConfigSortOrder);
    if (isNewChunk) {
      relatedGuidesChunks.push([...currentChunk]);
      currentChunk = [guide];
      isCurrentChunkForGuidesWithMenuConfigSortOrder =
        !isCurrentChunkForGuidesWithMenuConfigSortOrder;
    } else {
      currentChunk.push(guide);
    }
    if (i === relatedGuides.length - 1) {
      relatedGuidesChunks.push([...currentChunk]);
    }
  });
  const sortedRelatedGuidesChunks = relatedGuidesChunks.map((chunk) =>
    chunk.sort(bySortOrder)
  );
  return flatten(sortedRelatedGuidesChunks);
};

export const getSortOrder = (data) =>
  data && typeof data.sortOrder === 'number' ? data.sortOrder : null;
